import React from 'react';
import { useSelector } from 'react-redux';
import { DeleteOutlined } from '@ant-design/icons';
import { Collapse, Popconfirm, Tag, Select, Segmented } from 'antd';
import RemedyEditor, { queryLanguageFor } from '../util/monaco-remedy';
import FieldsEditor from './FieldsEditor';
import RedactionChip from '../common/RedactionChip';

const { Panel } = Collapse;

// strips empty cells so the saved config stays clean
export const rowsToFieldsObject = (rows) => {
    const fields = {};
    rows.forEach((row) => {
        const { field, key, ...def } = row;
        if (!field) return;
        const cleanDef = {};
        Object.keys(def).forEach((k) => {
            if (def[k] !== undefined && def[k] !== null && def[k] !== '') {
                cleanDef[k] = def[k];
            }
        });
        fields[field] = cleanDef;
    });
    return fields;
};

/**
 * Editable panel for textConfig and anonymizeText entries.
 * entry: {form, query, fields:{FIELD:{action,size,value}}}
 */
const TextPanel = ({ entry, formFields, actions, onChange, onDelete, index, connectors, profiles }) => {
    const isMlEntry = (actions || []).indexOf('removePII') !== -1;
    const ql = queryLanguageFor(connectors, entry.connector);
    const rows = Object.keys(entry.fields || {}).map((field) => ({ field, ...entry.fields[field] }));

    // entity types to suggest in the per-type exception picker. These MUST
    // match the type names the ML service returns in analysis[].type (verified
    // live: PERSON, PHONE, EMAIL, MONEY, BANK_ACCOUNT - note it's BANK_ACCOUNT,
    // not IBAN). Free entry is allowed too, and we merge in configured tags.
    const ML_TYPES = ['PERSON', 'PHONE', 'EMAIL', 'MONEY', 'BANK_ACCOUNT'];
    const allEntities = useSelector((s) => s.request && s.request.all_entities) || [];
    const typeSet = new Set(ML_TYPES);
    allEntities.forEach((e) => { const t = (e && e.tag) || e; if (t) typeSet.add(String(t).toUpperCase()); });
    const typeOptions = Array.from(typeSet).map((t) => ({ value: t, label: t }));

    const mode = entry.mode || 'auto';
    // in auto mode the exception list is the types to HOLD for review;
    // in review mode it is the types to AUTO-apply. same field/record can mix.
    const exceptionKey = mode === 'auto' ? 'reviewTypes' : 'autoTypes';
    const exceptionValue = entry[exceptionKey] || [];
    const setExceptions = (vals) => {
        const up = (vals || []).map((v) => String(v).toUpperCase());
        onChange({ ...entry, reviewTypes: undefined, autoTypes: undefined, [exceptionKey]: up.length ? up : undefined });
    };

    const genExtra = () => (
        <Popconfirm
            title={`Remove ${entry.form} from configuration?`}
            onConfirm={(e) => { e.stopPropagation(); onDelete(); }}
            onCancel={(e) => e.stopPropagation()}
        >
            <DeleteOutlined onClick={(event) => event.stopPropagation()} />
        </Popconfirm>
    );

    return (
        <div className={"sc-entry"} style={{marginBottom: 16}}>
        <Collapse ghost defaultActiveKey={['1']}>
            <Panel key={1} header={<span className="sc-entry-header"><span className="etype" style={{fontWeight: 680}}>{entry.form}</span><Tag color="geekblue" style={{marginLeft: 8}}>{entry.connector || "remedy"}</Tag>{isMlEntry && entry.mode === 'review' && (!entry.autoTypes || !entry.autoTypes.length) && <Tag color="orange" style={{marginLeft: 4}}>review</Tag>}{isMlEntry && entry.reviewTypes && entry.reviewTypes.length ? <Tag color="orange" style={{marginLeft: 4}}>{entry.reviewTypes.length} type{entry.reviewTypes.length > 1 ? 's' : ''} → review</Tag> : null}{isMlEntry && entry.autoTypes && entry.autoTypes.length ? <Tag color="green" style={{marginLeft: 4}}>{entry.autoTypes.length} type{entry.autoTypes.length > 1 ? 's' : ''} → auto</Tag> : null}{isMlEntry && entry.profile && <Tag color="purple" style={{marginLeft: 4}}>{entry.profile}</Tag>}</span>} extra={genExtra()}>
                {isMlEntry && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12}}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                            <span className="sc-dim" style={{fontSize: 12, fontWeight: 600}}>Mode</span>
                            <Segmented
                                value={mode}
                                onChange={(v) => onChange({ ...entry, mode: v === 'auto' ? undefined : v, reviewTypes: undefined, autoTypes: undefined })}
                                options={[
                                    {value: 'auto', label: 'Auto'},
                                    {value: 'review', label: 'Review'}
                                ]}
                            />
                        </span>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                            <span className="sc-dim" style={{fontSize: 12, fontWeight: 600}}>
                                {mode === 'auto' ? 'Always review' : 'Auto-apply'}
                            </span>
                            <Select
                                mode="tags"
                                size="small"
                                allowClear
                                value={exceptionValue}
                                onChange={setExceptions}
                                options={typeOptions}
                                style={{minWidth: 200}}
                                placeholder={mode === 'auto' ? 'e.g. MONEY, BANK_ACCOUNT' : 'e.g. PHONE, EMAIL'}
                                tokenSeparators={[',', ' ']}
                            />
                            <span className="sc-dim" style={{fontSize: 11.5, color: 'var(--sc-ink-3)'}}>
                                {mode === 'auto' ? 'these types wait for approval, the rest apply automatically'
                                                 : 'these types apply automatically, the rest wait for approval'}
                            </span>
                        </span>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                            <span className="sc-dim" style={{fontSize: 12, fontWeight: 600}}>Detection profile</span>
                            <Select
                                size="small"
                                value={entry.profile || ''}
                                onChange={(v) => onChange({ ...entry, profile: v || undefined })}
                                style={{width: 170}}
                                options={[{value: '', label: 'Default'}, ...((profiles || []).map((x) => ({value: x, label: x})))]}
                            />
                        </span>
                        <span className="sc-pill warn">profile: {entry.profile || 'default'}</span>
                    </div>
                )}
                {isMlEntry && (
                    <div className="sc-subcard" style={{marginBottom: 12}}>
                        <div className="sc-panel-cap" style={{marginBottom: 8}}>Preview</div>
                        <div style={{fontSize: 13.5, lineHeight: 1.7}}>
                            <span style={{textDecoration: 'line-through', color: 'var(--sc-ink-3)'}}>Please call Mr Smith on 0170 1234567</span>
                        </div>
                        <div style={{fontSize: 13.5, lineHeight: 1.7, marginTop: 4}}>
                            Please call <RedactionChip type="PERSON"/> on <RedactionChip type="PHONE"/>
                        </div>
                    </div>
                )}
                <div style={{fontSize: 12, color: "var(--sc-ink-3)", marginBottom: 4}}>
                    Query ({ql.name}){ql.example && <span style={{marginLeft: 8, color: "#bfbfbf"}}>e.g. {ql.example}</span>}
                </div>
                <RemedyEditor
                    key={`text-bq-editor-${index}`}
                    queryLanguage={ql}
                    height={'60px'}
                    minimap={{ enabled: false }}
                    defaultValue={entry.query}
                    formFields={formFields}
                    form={entry.form}
                    onChange={(v) => onChange({ ...entry, query: v })}
                />
                <FieldsEditor
                    rows={rows}
                    formFields={formFields}
                    columns={[
                        { key: 'field', title: 'Field', type: 'field' },
                        { key: 'action', title: 'Action', type: 'select', options: actions },
                        { key: 'size', title: 'Size', type: 'number', width: 110 },
                        { key: 'value', title: 'Value', type: 'text' }
                    ]}
                    onChange={(newRows) => onChange({ ...entry, fields: rowsToFieldsObject(newRows) })}
                />
            </Panel>
        </Collapse>
        </div>
    );
};

export default TextPanel;
